
import React from "react";
/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { vec2 } from "@tlaukkan/tsm";
import { MouseEventHandler } from "react";
import { ElementDisplayInfo } from "../../../shared/elementDisplayInfo";
import { GameElement } from "../../../shared/gameElementTypes";
import { cellLocations } from '../../../shared/generated/assetLocations';
import { canAffordImmuneCell, hasRoom } from "../../../shared/player";
import { GameClient } from "../gameClient";
import { coloredSpritesheet } from "../rendering/spriteFactory";
import { ImmuneCellButton } from "../styles/ButtonStyle";
import { CostMarker, FreeMarker, TierMarker } from "./TierMarker";
import { AssetLocation } from "../../../shared/utils";
import { Sound } from "../../../shared/sound";
import { CameraZoomLevel } from "../../../shared/constants";
import { Colors } from "../palettes/colorPalette";

type ZoomWidgetProps = {
    zoomLevel: CameraZoomLevel;
}

declare var gameClient: GameClient;

const zoomWidgetStyle = css`
    position: absolute;
    display: flex;
    margin-top: 1vh;
    top: 0;
    left: 50%;
    transform: translate(-50%, 0);
    font-size: 2em;
    color: white;
    text-shadow: 1px 1px 1px black;
`;

const zoomNumberStyle = css`
    text-decoration: underline;
    margin-left: 1vw;
    margin-right: 1vw;
`;

const selectedStyle = css`
    transform: scale(1);
    text-decoration-color: ${Colors.UIIncreasedStat};
`;

const unselectedStyle = css`
    transform: scale(.7);
    text-decoration-color: transparent;
    opacity: 0.5;
`;

const autoButtonDisabled = css`
    border: 2px solid white;
    opacity: 0.5;

    color: white;
    border-radius: 0.5em;
    font-size: 0.5em;
    padding: 0.15em;
`;

const autoButtonEnabled = css`
    border: 3px solid white;
    text-decoration: underline;

    color: white;
    border-radius: 0.5em;
    font-size: 0.5em;
    padding: 0.15em;
`

const autoButtonContainer = css`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    margin-left: 1vw
`;


const zoomNameTranslation = {
    [CameraZoomLevel.Far]: "400x",
    [CameraZoomLevel.Medium]: "800x",
    [CameraZoomLevel.Normal]: "1000x"
}

export const ZoomWidget = ({ zoomLevel }: ZoomWidgetProps) => {

    return (<div css={zoomWidgetStyle}>
        <ZoomNumber
            selected={zoomLevel === CameraZoomLevel.Far}
            zoomLevel={CameraZoomLevel.Far}
        />
        <ZoomNumber
            selected={zoomLevel === CameraZoomLevel.Medium}
            zoomLevel={CameraZoomLevel.Medium}
        />
        <ZoomNumber
            selected={zoomLevel === CameraZoomLevel.Normal}
            zoomLevel={CameraZoomLevel.Normal}
        />
        {gameClient.inSimulation && <AutoButton />}
    </div>);
}

type ZoomNumberProps = {
    zoomLevel: CameraZoomLevel;
    selected: boolean;
}

const ZoomNumber = ({ zoomLevel, selected }: ZoomNumberProps) => {
    const hover = () => {
        if (gameClient) {
            gameClient.playUISound(Sound.Menu);
        }
    }

    const setZoom = () => {
        if (gameClient) {
            gameClient.cameraSystem.setZoomLevel(zoomLevel);
            gameClient.updateUI();
        }
    }

    return (<div
        onMouseEnter={hover}
        onClick={setZoom}
        css={css`${zoomNumberStyle} ${selected ? selectedStyle : unselectedStyle}`}
    >
        {zoomNameTranslation[zoomLevel]}
    </div>);
};

const AutoButton = () => {
    const hover = () => {
        if (gameClient) {
            gameClient.playUISound(Sound.Menu);
        }
    }

    const click = () => {
        if (gameClient && gameClient.inSimulation) {
            gameClient.toggleAutoCamera(!gameClient.autoCameraActive);
            gameClient.updateUI();
        }
    }

    return <div css={autoButtonContainer}
        onClick={click}
        onMouseEnter={hover}
    >
        <div css={gameClient.autoCameraActive ? autoButtonEnabled : autoButtonDisabled}>Auto</div>
    </div>
}
